<template>
	<template v-if="hasSubmenu">
		<anchor-element class="st-item" :class="itemClasses" tag="div" :chain="chain" @mouseenter="selectMe">
			<span class="st-icon-text" ref="itemIconText">
				<sym-icon :style="{ visibility: onPath ? 'visible':'hidden' }" name="checkmark-14-bold" style="opacity: 0.3"></sym-icon>
				<span class="st-text st-folder">{{ item.text || item.id }}</span>
			</span>
			<sym-icon name="caret-right-12" class="st-caret"></sym-icon>
		</anchor-element>
		<boat-element :tag="SelectTreePanel" :vertical-correction="true" :items="item.submenu.items" v-model="dataValue" v-model:selection="selectedValue" class="fd-drop fd-form" :chain="chain" :dir="direction"></boat-element>
	</template>
	<div v-else class="st-item" :class="itemClasses" @pointerup="pickMe" @mouseenter="selectMe">
		<span class="st-icon-text" ref="itemIconText">
			<sym-icon :style="{ visibility: onPath ? 'visible':'hidden' }" name="checkmark-14-bold"></sym-icon>
			<span class="st-text">{{ item.text || item.id }}</span>
		</span>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, watch, nextTick, inject, type Ref} from "vue";
import type {SelectHierarchyItem} from "@t/ui/SelectHierarchy.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {chainContext, createChain, DROPDOWN_PLACEMENT, DROPUP_PLACEMENT} from "@/ui/paneltree/PanelTree.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import SelectTreePanel from "@/ui/select/SelectTreePanel.vue";
import {ElementMeasure} from "@fd/helpers/DomUtils.ts";
import {findItemIn} from "@/ui/select/SelectTreeUtils.ts";

const props = defineProps<{
	item: SelectHierarchyItem
	modelValue?: string,
	selection?: string,
	dir: "left" | "right"
}>();

const pointerDownTimestamp = inject("pointerDownTimestamp") as Ref<number>;
const emit = defineEmits(['update:modelValue', 'update:selection']);
const hasSubmenu = computed<boolean>(() => Array.isArray(props.item?.submenu?.items));
const onSelectionPath = computed<boolean>(() => {
	const v = props.selection;
	if (props.item.id === v) return true;
	if (Array.isArray(props.item?.submenu?.items)) {
		return findItemIn(v, props.item.submenu.items) != null;
	}
	return false;
});
/** True if current item is selected */
const selected = computed<boolean>(() => props.item.id === props.selection);
const itemIconText = ref<HTMLSpanElement | null>(null);

const chain = hasSubmenu.value ? createChain({
	visible: onSelectionPath.value,
	rule: (m:ElementMeasure) => {
		if ("right" === direction.value) {
			return {
				ah: 100, av: 50,
				bh: 0, bv: 50,
				x: -4, y: 0
			}
		} else {
			return {
				ah: 0, av: 50,
				bh: 100, bv: 50,
				x: 4, y: 0
			}
		}
	}
}, "st-"+props.item.id) : null;
const chains = chainContext();

const direction = computed<"left" | "right">(() => {
	if (chain?.anchor.value) {
		if ("left" === props.dir) {
			if (chain.anchor.value.spaceLeft < 100) return "right";
			return "left";
		} else {
			if (chain.anchor.value.spaceRight < 100) return "left";
			return "right";
		}
	}
	return props.dir;
});

const onPath = computed<boolean>(() => {
	const v = props.modelValue;
	if (props.item.id === v) return true;
	if (Array.isArray(props.item?.submenu?.items)) {
		return findItemIn(v, props.item.submenu.items) != null;
	}
	return false;
});

const dataValue = computed<string>({
	get():string {
		return props.modelValue;
	},
	set(v: string):void {
		emit('update:modelValue', v);
	}
});

const selectedValue = computed<string>({
	get():string {
		return props.selection;
	},
	set(v: string):void {
		emit('update:selection', v);
	}
});

const picking = ref<boolean>(false);
const itemClasses = computed<string[]>(() => {
	const cls = [];
	if (props.selection === props.item.id) cls.push('st-highlight');
	if (picking.value) cls.push('item-pick-animation')
	return cls;
});

function selectMe() {
	selectedValue.value = props.item.id;
}

function pickMe(e:PointerEvent) {
	const timeDiff = e.timeStamp - pointerDownTimestamp.value;
	if (timeDiff < 500) {
		// Too little delay (0.5s) - doesn't count as click
		e.preventDefault();
		return;
	}
	picking.value = true;
	dataValue.value = props.item.id;
	setTimeout(() => {
		picking.value = false;
	}, 150);
}

let submenuOpenTimeout = 0;

function cancelOpenSubmenu() {
	if (submenuOpenTimeout) {
		//console.log("Cancelling submenu open: " + props.item.id);
		clearTimeout(submenuOpenTimeout);
		submenuOpenTimeout = 0;
	}
}

function openNow() {
	chains.closePanels(false, "st-" + props.item.id);	// close other panels
	chain.visible.value = true;
	submenuOpenTimeout = 0;
	//console.log("...Success openned panel " + props.item.id);
}

function openSubmenuDelayed() {
	cancelOpenSubmenu();
	//console.log("Planned open submenu "+props.item.id+" in 200ms");
	submenuOpenTimeout = setTimeout(openNow, 200);
}

let siblingCloseTimeout = 0;
function cancelCloseSiblingSubmenus() {
	if (siblingCloseTimeout) {
		//console.log("Cancelling sibling close for " + props.item.id);
		clearTimeout(siblingCloseTimeout);
		siblingCloseTimeout = 0;
	}
}

function closeSiblingSubmenus() {
	cancelCloseSiblingSubmenus();
	//console.log("Planned close siblings of "+props.item.id+" in 200ms");
	siblingCloseTimeout = setTimeout(() => {
		chains.closePanels(false, "st-" + props.item.id);
		//console.log("...Success siblings closed " + props.item.id);
		siblingCloseTimeout = 0;
	}, 200);
}

function itemSelect() {
	//console.log("selected "+props.item.id);
	if (hasSubmenu.value) openSubmenuDelayed();
	else closeSiblingSubmenus();
	nextTick(() => {
		if (itemIconText.value) itemIconText.value.scrollIntoView({
			behavior: 'smooth', // Smooth scrolling
			block: 'nearest', // Scroll to the nearest edge of the container
			inline: 'nearest' // Scroll to the nearest edge of the container horizontally
		});
	});
}

function itemUnselect() {
	//console.log("unselected "+props.item.id);
	if (hasSubmenu.value) cancelOpenSubmenu();
}

watch(selected, (sel) => {
	if (sel) itemSelect();
	else itemUnselect();
});
</script>

<style>
.st-item {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
	align-items: baseline;
	justify-content: space-between;
	line-height: var(--input-height);
	height: var(--input-height);
	background-color: var(--bg);
	color: var(--fg);
	border-radius: var(--button-radius);
	padding-left: 4px;
	padding-right: 4px;
	cursor: default;
	max-width: 100%;
	box-sizing: border-box;
	overflow: hidden;
	flex-shrink: 0;

	& > span {
		display: flex;
		flex-direction: row;
		column-gap: 4px;
		align-items: baseline;
		justify-content: flex-start;
		flex-shrink: 0;
		flex-grow: 0;
	}

	&.st-item.st-highlight {
		--bg: var(--selection-bg);
		--fg: var(--selection-fg);

		&.item-pick-animation {
			animation: selection-blink 120ms;
		}
	}
}
.st-folder {
	font-weight: 500;
}
.st-text {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: var(--select-tree-menu-item-width, 300px);
}
.st-caret {
	opacity: 0.5;
	flex: 0 0;
}
@keyframes selection-blink {
	0%   {opacity: 1;}
	50%  {opacity: 0;}
	100% {opacity: 1;}
}

</style>